// Dark mode (docs/dark-mode.md). Follows the OS setting (FOLLOW_SYSTEM), live
// while the page is open. The footer switch or `?theme=dark|light` overrides
// it, but only until the OS setting itself changes: an override is stored
// together with the OS scheme it was made against, and dropped (back to
// following the OS) once that differs. Picking the OS's own scheme simply
// clears the override. Set FOLLOW_SYSTEM to false to make dark mode opt-in.
import { useSyncExternalStore } from 'react'

export const FOLLOW_SYSTEM = true

// localStorage value: '<dark|light>@<OS scheme when chosen>', e.g. 'light@dark'.
const KEY = 'theme'
// Literal colours for places CSS variables can't reach (the iOS
// <meta name="theme-color">, MapLibre paint, the cross-origin Funraise iframe).
// Keep in sync with --color-surface and --color-royal in app.css.
export const SURFACE_COLOR = { light: '#ffffff', dark: '#121417' }
export const ROYAL = '#225dd6'

/**
 * Inline script for <head>: adds `.dark` to <html> before first paint so
 * there's no flash of the wrong theme. Mirrors setTheme() below. It also owns
 * the iOS status bar's <meta name="theme-color"> (white header / dark
 * surface), creating it: if React rendered that meta, it would add a second
 * one next to this script's changed copy on hydration.
 */
export const themeScript = `(function(d,w){try{
var s=w.localStorage,mq=w.matchMedia('(prefers-color-scheme: dark)');
function os(){return ${FOLLOW_SYSTEM}&&mq.matches?'dark':'light'}
function apply(dark){d.documentElement.classList.toggle('dark',dark);
var m=d.querySelector('meta[name="theme-color"]');
if(!m){m=d.createElement('meta');m.name='theme-color';d.head.appendChild(m)}
m.setAttribute('content',dark?'${SURFACE_COLOR.dark}':'${SURFACE_COLOR.light}')}
var q=new URLSearchParams(w.location.search).get('theme');
if(q==='dark'||q==='light'){if(q===os())s.removeItem('${KEY}');else s.setItem('${KEY}',q+'@'+os())}
var v=(s.getItem('${KEY}')||'').split('@');
if(v[0]&&v[1]!==os()){s.removeItem('${KEY}');v=[]}
apply((v[0]||os())==='dark');
mq.addEventListener('change',function(){s.removeItem('${KEY}');apply(os()==='dark')});
}catch(e){}})(document,window)`

const osScheme = () =>
  FOLLOW_SYSTEM && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'

function applyTheme(dark: boolean) {
  document.documentElement.classList.toggle('dark', dark)
  let meta = document.querySelector('meta[name="theme-color"]')
  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute('name', 'theme-color')
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', dark ? SURFACE_COLOR.dark : SURFACE_COLOR.light)
}

/**
 * Re-applies the theme the head script chose. For when React has rebuilt
 * <html> and <head> since the script ran (the SPA shell is rendered, not
 * hydrated; src/client.tsx), which drops the class and the theme-color.
 */
export function restoreTheme() {
  let choice: string | undefined
  try {
    const [value, chosenAgainst] = (localStorage.getItem(KEY) ?? '').split('@')
    if (value && chosenAgainst === osScheme()) choice = value
  } catch {
    // Storage blocked: follow the OS.
  }
  applyTheme((choice ?? osScheme()) === 'dark')
}

export function setTheme(dark: boolean) {
  applyTheme(dark)
  try {
    const choice = dark ? 'dark' : 'light'
    const os = osScheme()
    if (choice === os) localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, `${choice}@${os}`)
  } catch {
    // Storage blocked: the choice lasts for this page only.
  }
}

/** Whether dark mode is on right now (the `dark` class on <html>). */
export const isDark = () => document.documentElement.classList.contains('dark')

function subscribe(onChange: () => void) {
  const obs = new MutationObserver(onChange)
  obs.observe(document.documentElement, { attributeFilter: ['class'] })
  return () => obs.disconnect()
}

/** Whether dark mode is on. False during prerender and hydration. */
export function useDarkMode() {
  return useSyncExternalStore(subscribe, isDark, () => false)
}
